// Ocean Insight Pro Mobile - Trips Redux Slice

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TripPlan, TripReport } from '../types';
// import { WAYPOINTS } from '../services/mockData'; // Temporarily disabled - waypoints loaded async now

// Define the trips state interface
interface TripsState {
  plans: TripPlan[];
  reports: TripReport[];
  current: TripPlan | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: TripsState = {
  plans: [],
  reports: [],
  current: null,
  loading: false,
  error: null,
};

// Mock trip data for development - temporarily using hardcoded coordinates
const mockTripPlans: TripPlan[] = [
  {
    id: 'trip-1',
    name: 'St. Augustine Fishing Circuit',
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    departureLocation: { latitude: 29.9012, longitude: -81.3124 }, // St. Augustine Inlet
    waypoints: [
      { latitude: 29.9012, longitude: -81.3124 },
      { latitude: 29.8500, longitude: -81.2500 },
      { latitude: 29.8000, longitude: -81.2000 },
      { latitude: 29.7500, longitude: -81.1500 },
      { latitude: 29.7000, longitude: -81.1000 }
    ], // Sample waypoints
    targetSpecies: ['mahi-mahi', 'wahoo', 'yellowfin-tuna'],
    estimatedFuelConsumption: 50, // gallons
    estimatedTravelTime: 8, // hours
    weatherWindow: {
      acceptable: true,
      concerns: []
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
];

// Async thunks for API calls (using mock data for now)
export const fetchTripPlans = createAsyncThunk('trips/fetchPlans', async (): Promise<TripPlan[]> => {
  await new Promise<void>((resolve) => setTimeout(resolve, 1000));
  return mockTripPlans;
});

export const createTripPlan = createAsyncThunk(
  'trips/createPlan',
  async (plan: Omit<TripPlan, 'id' | 'createdAt' | 'updatedAt'>): Promise<TripPlan> => {
    await new Promise<void>((resolve) => setTimeout(resolve, 800));

    const newPlan: TripPlan = {
      ...plan,
      id: `trip-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return newPlan;
  }
);

// Trips slice
const tripsSlice = createSlice({
  name: 'trips',
  initialState,
  reducers: {
    setCurrentTrip: (state, action: PayloadAction<TripPlan | null>) => {
      state.current = action.payload;
    },
    clearTripsData: (state) => {
      state.plans = [];
      state.reports = [];
      state.current = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTripPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
        state.loading = false;
      })
      .addCase(createTripPlan.fulfilled, (state, action) => {
        state.plans.push(action.payload);
        state.loading = false;
      });
  },
});

export const { setCurrentTrip, clearTripsData } = tripsSlice.actions;
export default tripsSlice.reducer;
